<template>
  <div class="wrapper">
    <MobileHeader ref="header" class="header desktop:hidden" />
    <DesktopHeader ref="header" class="header mobile:hidden" />
    <main>
      <div ref="container">
        <!--        <MBreadCrumb />-->
        <router-view />
      </div>
    </main>

    <MobileFooter ref="footer" class="footer desktop:hidden" />
    <DesktopFooter ref="footer" class="footer mobile:hidden" />
    <MAlertDialog />
    <MConfirmDialog />
    <MModal ref="selectMembershipModal" @close="handleCloseSelectMemberModal">
      <template #modal-body>
        <!--타이틀-->
        <div class="sub-title">
          <h1>회원권변경</h1>
          <p>Change of membership</p>
        </div>

        <div class="change-pop-wrap">
          <p class="sub-text">아래 변경하실 회원권 번호를 선택해 주세요.</p>

          <div class="change-mem-select">
            <MFormField id="membership" class="membership">
              <ASelectBox
                class="custom-select"
                :optionList="selectOptions"
                v-model="selectMembership"
              />
            </MFormField>
          </div>
        </div>

        <!--버튼-->
        <div class="common-btn mt80">
          <AButton @click="handleCloseSelectMemberModal">닫기</AButton>
          <AButton class="on" @click="selectMembershipConfirm">선택</AButton>
        </div>
      </template>
    </MModal>
    <MQuickMenu />
    <MainPopup />
  </div>
</template>
<script setup lang="ts">
import MobileHeader from '@components/common/MobileHeader.vue';
import MobileFooter from '@components/common/MobileFooter.vue';
import DesktopHeader from '@components/common/DesktopHeader.vue';
import DesktopFooter from '@components/common/DesktopFooter.vue';
import MainPopup from '@components/common/MainPopup.vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useCommonStore, useUserStore } from '@stores';
import {
  MAlertDialog,
  MConfirmDialog,
  MFormField,
  MModal,
  MQuickMenu,
} from '@molecules';
import { useRouter } from 'vue-router';
import { ROUTE_NAME } from '@constants';
import { AButton, ASelectBox } from '@atoms';
import { useCommon, useMembershipChange } from '@composables';
import { useAlertStore } from '@stores';
import { memberNoFormat } from '@core/utils';

const alertStore = useAlertStore();
const { memberDivName } = useCommon(['MEMBER_DIV']);
const { changeMembership } = useMembershipChange();
const mobileHeader = ref<HTMLElement | null>(null);
const desktopHeader = ref<HTMLElement | null>(null);
const mobileFooter = ref<HTMLElement | null>(null);
const desktopFooter = ref<HTMLElement | null>(null);
const container = ref<HTMLElement>();
const commonStore = useCommonStore();
const userStore = useUserStore();
const selectMembershipModal = ref();
const router = useRouter();
const selectOptions = computed(() =>
  userStore.membershipList.map((m) => ({
    label: `${memberDivName.value(m.memberDiv)} ${memberNoFormat(m.memberNo)}`,
    value: m.membershipId,
  }))
);
const selectMembership = ref<string>('');
const selectMembershipConfirm = async () => {
  try {
    await changeMembership(selectMembership.value);
    await alertStore.showAlertDialog('회원권이 변경되었습니다');
    handleCloseSelectMemberModal();
    if (localStorage.getItem('redirect')) {
      await router.push(localStorage.getItem('redirect') as string);
      localStorage.removeItem('redirect');
    } else {
      await router.push({ name: ROUTE_NAME.Main });
    }
    router.go(0);
  } catch (e) {
    console.error(e);
  }
};
const adjustContainerHeight = async () => {
  if (!container.value) return;
  const headerHeight = commonStore.isMobile
    ? mobileHeader.value?.offsetHeight || 0
    : desktopHeader.value?.offsetHeight || 0;

  const footerHeight = commonStore.isMobile
    ? mobileFooter.value?.offsetHeight || 0
    : desktopFooter.value?.offsetHeight || 0;

  container.value.style.minHeight = `calc(100vh - ${headerHeight + footerHeight}px)`;
};

onMounted(() => {
  adjustContainerHeight();
  window.addEventListener('resize', adjustContainerHeight);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', adjustContainerHeight);
});

const handleCloseSelectMemberModal = () => {
  commonStore.setMembershipChange(false);
};
watch(
  () => commonStore.isSigninOpen,
  (isSigninOpen: boolean) => {
    if (isSigninOpen) {
      router.push({ name: ROUTE_NAME.Signin });
    }
  },
  {
    immediate: true,
  }
);
watch(
  () => commonStore.isMembershipChangeFlag,
  (membershipChangeFlag: boolean) => {
    if (membershipChangeFlag && selectMembershipModal.value) {
      userStore.fetchMembershipList();
      selectMembership.value =
        sessionStorage.getItem('membershipId') || userStore.membershipIds[0];
      selectMembershipModal.value.open();
    }
    if (!membershipChangeFlag && selectMembershipModal.value) {
      selectMembershipModal.value.close();
    }
  }
);
</script>

<style scoped lang="scss">
.menu-size {
  @apply pt-72;
  @apply desktop:pt-170;
}
main {
  padding-bottom: env(safe-area-inset-bottom);
  @apply bg-white;
}
.wrapper {
  position: relative;
  .container {
    @apply desktop:w-[1200px] desktop:m-auto;
  }
}
//타이틀
.sub-title {
  text-align: center;
  padding: 35px 0 17px;
  h1 {
    font-size: 33px;
    margin-bottom: 10px;
    font-weight: 700;
  }
  p {
    font-size: 14px;
    color: #847f7f;
  }
}

//컨텐츠
.change-pop-wrap {
  margin: 17px auto 55px;
  text-align: center;
  width: 78%;
  .con-text {
    color: #3f3f3f;
    font-size: 16px;
  }

  //셀렉트박스
  .change-mem-select {
    border-top: 2px solid var(--main-color);
    border-bottom: 1px solid #b4b4b4;
    padding: 40px 0;
    margin-top: 24px;
  }
  .membership {
    justify-content: center;
  }

  //셀렉트박스 넓이값 지정
  .custom-select {
    width: 373px;
  }
}

//버튼
.common-btn {
  width: 100%;
  margin: 10px 0 55px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0 18px;
  button {
    width: 160px;
    height: 54px;
    background: #fff;
    border: 1px solid #c2c2c2;
    color: #6f6f6f;
    font-size: clamp(1rem, 0.975rem + 0.125vw, 1.125rem);
    border-radius: 20px 0px 20px 1px;
  }
  .on {
    background: var(--btn-color);
    color: #fff;
    border: 1px solid var(--btn-color);
  }
}

/*모바일*/
@media (min-width: 320px) and (max-width: 1279px) {
  .sub-title {
    padding: 35px 0;
    h1 {
      font-size: 1.38rem;
    }

    p {
      font-size: 0.75rem;
    }
  }
  .sub-text {
    font-size: 0.75rem;
    margin: 0 0 13px 0;
  }

  .change-pop-wrap {
    margin: 0 auto 30px auto;
    text-align: center;
    width: 100% !important;
    .con-text {
      color: #3f3f3f;
      font-size: 16px;
    }
  }
  //버튼
  .common-btn {
    margin: 10px 0 40px 0;
    gap: 0 15px;
    padding: 0 16px;
    button {
      width: 50%;
      height: 40px;
    }
  }
}
</style>
